/**
 * See the NOTICE file distributed with this work for additional information
 * regarding copyright ownership.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import * as urlFor from 'src/shared/helpers/urlHelper';
import useHeaderAnalytics from '../hooks/useHeaderAnalytics';

import { GlobalSearchIcon, HelpIcon } from 'src/shared/components/app-icon';
import LaunchbarButton from './LaunchbarButton';
import SpeciesSelectorLaunchbarButton from './SpeciesSelectorLaunchbarButton';
import GenomeBrowserLaunchbarButton from './GenomeBrowserLaunchbarButton';
import EntityViewerLaunchbarButton from './EntityViewerLaunchbarButton';
import AlignmentsViewerLaunchbarButton from './AlignmentsViewerLaunchbarButton';
import RegulatoryActivityViewerLaunchbarButton from './RegulationViewerLaunchbarButton';
import BlastLaunchbarButton from './BlastLaunchbarButton';
import VepLaunchbarButton from './VepLaunchbarButton';
import EnsemblMarkIcon from 'src/shared/components/ensembl-mark-icon/EnsemblMarkIcon';

import Logotype from 'static/img/brand/logotype.svg';

import styles from './Launchbar.module.css';

const Launchbar = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const withBottomBorder = pathname !== urlFor.home();

  const componentClasses = classNames(styles.launchbar, {
    [styles.launchbarWithBottomBorder]: withBottomBorder
  });

  return (
    <div className={componentClasses}>
      <div className={styles.categoriesWrapper}>
        <div className={styles.categories}>
          <div className={styles.category}>
            <LaunchbarButton
              path="/global-search"
              description="Site search"
              icon={GlobalSearchIcon}
              enabled={false}
            />
            <SpeciesSelectorLaunchbarButton />
          </div>
          <div className={styles.category}>
            <GenomeBrowserLaunchbarButton />
            <AlignmentsViewerLaunchbarButton />
          </div>
          <div className={styles.category}>
            <EntityViewerLaunchbarButton />
            <RegulatoryActivityViewerLaunchbarButton />
          </div>
          <div className={styles.category}>
            <BlastLaunchbarButton />
            <VepLaunchbarButton />
          </div>
          <div className={styles.category}>
            <LaunchbarButton
              path="/help"
              description="Help & documentation"
              icon={HelpIcon}
              enabled={true}
            />
          </div>
        </div>
      </div>
      <AboutEnsembl />
    </div>
  );
};

const AboutEnsembl = () => {
  const { trackLaunchbarAppChange } = useHeaderAnalytics();

  return (
    <div className={styles.aboutEnsemblWrapper}>
      <Link
        to="/about"
        className={styles.aboutEnsembl}
        onClick={() => trackLaunchbarAppChange('About')}
      >
        About the
        <Logotype className={styles.logotype} />
        project
      </Link>

      <EnsemblMarkIcon className={styles.ensemblMark} />
    </div>
  );
};

export default Launchbar;
